import { useCallback } from 'react';
import { Location, NavigateFunction, useLocation, useNavigate } from 'react-router-dom';

import { RedirectToOptions, UseRedirectTo } from '../interfaces';
import { getUrlByRouteName } from '../utils';

export const useRedirectTo: () => UseRedirectTo = (): UseRedirectTo => {
  const location: Location = useLocation();
  const navigate: NavigateFunction = useNavigate();

  const redirect: (redirectOptions: RedirectToOptions) => void = useCallback((redirectOptions: RedirectToOptions): void => {
    const { route, params, searchParams, isExternalUrl, inNewTab, goBack, replace, hash, externalUrl }: RedirectToOptions = redirectOptions;
    if (goBack) {
      navigate(-1);

      return;
    }

    if (hash && !route) {
      navigate(`#${hash}`, { replace });

      return;
    }

    const targetRoute: string | undefined = externalUrl ?? (route ? getUrlByRouteName(route)?.url : undefined);

    if (!targetRoute || (targetRoute === location.pathname && !replace && !hash)) {
      return;
    }

    let modifiedPath: string = targetRoute;
    const paramsEntries: [string, string | number][] = Object.entries(params ?? {});
    for (const [key, value] of paramsEntries) {
      modifiedPath = modifiedPath.replace(`:${key}`, encodeURIComponent(value.toString()));
    }

    if (searchParams && Object.keys(searchParams).length > 0) {
      const query: URLSearchParams = new URLSearchParams();
      for (const [key, value] of Object.entries(searchParams)) {
        query.append(key, value.toString());
      }
      modifiedPath += `?${query.toString()}`;
    }

    if (hash) {
      modifiedPath += `#${hash}`;
    }

    if (isExternalUrl && !inNewTab) {
      window.location.assign(modifiedPath);
    } else if (inNewTab) {
      window.open(modifiedPath, '_blank')?.focus();
    } else {
      navigate(modifiedPath, { replace });
    }
  }, [navigate, location.pathname]);

  return { redirect };
};
